import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {DataService} from './data.service';
import {
  WoAsset,
  WoBillingSummary,
  WoDashboard,
  WoRecurringSchedule,
  WoType,
  WoCategory,
  WoUserBillingRate,
  WO_WORK_TYPES,
  WoWorkType,
  WoLinkedInvoice,
} from '../DataModels/workorder-model';

// ─── Lightweight param shapes ────────────────────────────────────────────────

export interface WoListParams {
  page?: number;
  pageSize?: number;
  company_id?: string;
  entity_id?: string;
  status?: string;
  priority?: string;
  type_id?: string;
  category_id?: string;
  billing_status?: string;
  asset_id?: string;
  assigned_user_id?: string;
  from_date?: string;
  to_date?: string;
}

export interface WoDashboardParams {
  from_date?: string;
  to_date?: string;
  company_id?: string;
  user_id?: string;
}

export interface WoScheduleParams {
  from_date: string;
  to_date: string;
  user_id?: string;
}

export interface WoGenerateInvoiceResult {
  invoice_id: string;
}

export interface WoChecklistResponse {
  template_name: string;
  complete: boolean;
  checklist_incomplete?: boolean;
  items: {
    id: string;
    description: string;
    required: boolean;
    response: string | null;
    notes: string;
  }[];
}

export interface WoCompleteResult {
  id: string;
  status: string;
  completed_date: string;
  checklist_incomplete: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────

@Injectable({providedIn: 'root'})
export class WorkOrderService {

  private readonly ep = 'WorkOrder';

  constructor(private data: DataService) {
  }

  // ── Work Order CRUD ────────────────────────────────────────────────────────

  getWorkOrders(params?: WoListParams): Observable<any> {
    if (params && Object.keys(params).length) {
      const qs = new URLSearchParams(params as any).toString();
      return this.data.getDataWithParams(this.ep, qs);
    }
    return this.data.getData(this.ep);
  }

  getWorkOrder(id: string): Observable<any> {
    return this.data.getData(this.ep, id);
  }

  createWorkOrder(body: object): Observable<any> {
    return this.data.post(this.ep, body);
  }

  updateWorkOrder(id: string, body: object): Observable<any> {
    return this.data.patchDataByArray(`${this.ep}/${id}`, body);
  }

  deleteWorkOrder(id: string): Observable<any> {
    return this.data.deleteDataById(this.ep, id);
  }

  getLinkedInvoices(id: string): Observable<WoLinkedInvoice[]> {
    return this.data.getData(`${this.ep}/${id}/invoices`);
  }

  deleteInvoice(woId: string, invoiceId: string): Observable<{ deleted: boolean; invoice_number: string; billing_status: string }> {
    return this.data.deleteDataById(`${this.ep}/${woId}/invoice`, invoiceId);
  }

  deleteAttachment(woId: string, attachmentId: string): Observable<any> {
    return this.data.deleteDataById(`${this.ep}/${woId}/attachment`, attachmentId);
  }

  // ── Lifecycle Transitions ──────────────────────────────────────────────────

  dispatch(id: string): Observable<any> {
    return this.data.patchDataByArray(`${this.ep}/${id}/dispatch`, {});
  }

  arrive(id: string): Observable<any> {
    return this.data.patchDataByArray(`${this.ep}/${id}/arrive`, {});
  }

  complete(id: string): Observable<WoCompleteResult> {
    return this.data.patchDataByArray(`${this.ep}/${id}/complete`, {});
  }

  // ── Billing ────────────────────────────────────────────────────────────────

  getBillingSummary(id: string): Observable<WoBillingSummary> {
    return this.data.getData(`${this.ep}/${id}/billing-summary`);
  }

  generateInvoice(id: string, lines: { id: string; item_id: string }[] = [], groupByType = false): Observable<WoGenerateInvoiceResult> {
    return this.data.post(`${this.ep}/${id}/generate-invoice`, {
      lines,
      group_by_type: groupByType,
    });
  }

  // ── Notifications ──────────────────────────────────────────────────────────

  sendNotification(id: string, message: string, to?: string, eventType: string = 'custom'): Observable<any> {
    const body: any = {event_type: eventType, message};
    if (to) {
      body.to = to;
    }
    return this.data.post(`${this.ep}/${id}/notify`, body);
  }

  // ── Checklist ─────────────────────────────────────────────────────────────

  getChecklist(id: string): Observable<WoChecklistResponse> {
    return this.data.getData(`${this.ep}/${id}/checklist`);
  }

  saveChecklist(id: string, responses: { checklist_item_id: string; response: string; notes: string }[]): Observable<any> {
    return this.data.post(`${this.ep}/${id}/checklist`, responses);
  }

  // ── Dashboard & Schedule ───────────────────────────────────────────────────

  getDashboard(params?: WoDashboardParams): Observable<WoDashboard> {
    if (params && Object.keys(params).length) {
      const qs = new URLSearchParams(params as any).toString();
      return this.data.getDataWithParams(`${this.ep}/dashboard`, qs);
    }
    return this.data.getData(`${this.ep}/dashboard`);
  }

  getSchedule(params: WoScheduleParams): Observable<any[]> {
    const qs = new URLSearchParams(params as any).toString();
    return this.data.getDataWithParams(`${this.ep}/schedule`, qs);
  }

  // ── Work Types (hardcoded enum — no HTTP call) ─────────────────────────────

  getWorkTypes(): Observable<typeof WO_WORK_TYPES> {
    return of(WO_WORK_TYPES);
  }

  // ── Assets ─────────────────────────────────────────────────────────────────

  getAssets(params?: { entity_id?: string; location_id?: string; overdue_service?: boolean }): Observable<WoAsset[]> {
    if (params && Object.keys(params).length) {
      const qs = new URLSearchParams(params as any).toString();
      return this.data.getDataWithParams('wo_asset', qs);
    }
    return this.data.getData('wo_asset');
  }

  getAsset(id: string): Observable<WoAsset> {
    return this.data.getData('wo_asset', id);
  }

  createAsset(body: Partial<WoAsset>): Observable<WoAsset> {
    return this.data.post('wo_asset', body);
  }

  updateAsset(id: string, body: Partial<WoAsset>): Observable<WoAsset> {
    return this.data.patchDataByArray(`wo_asset/${id}`, body);
  }

  deleteAsset(id: string): Observable<any> {
    return this.data.deleteDataById('wo_asset', id);
  }

  getAssetHistory(assetId: string): Observable<any[]> {
    return this.data.getData(`wo_asset/${assetId}/history`);
  }

  // ── PM Schedules ───────────────────────────────────────────────────────────

  getPmSchedules(params?: { due_by?: string; entity_id?: string; active?: boolean }): Observable<WoRecurringSchedule[]> {
    if (params && Object.keys(params).length) {
      const qs = new URLSearchParams(params as any).toString();
      return this.data.getDataWithParams('wo_recurring_schedule', qs);
    }
    return this.data.getData('wo_recurring_schedule');
  }

  getPmSchedule(id: string): Observable<WoRecurringSchedule> {
    return this.data.getData('wo_recurring_schedule', id);
  }

  createPmSchedule(body: Partial<WoRecurringSchedule>): Observable<WoRecurringSchedule> {
    return this.data.post('wo_recurring_schedule', body);
  }

  updatePmSchedule(id: string, body: Partial<WoRecurringSchedule>): Observable<WoRecurringSchedule> {
    return this.data.patchDataByArray(`wo_recurring_schedule/${id}`, body);
  }

  deletePmSchedule(id: string): Observable<any> {
    return this.data.deleteDataById('wo_recurring_schedule', id);
  }

  generateFromSchedule(scheduleId: string): Observable<any> {
    return this.data.post(`wo_recurring_schedule/${scheduleId}/generate`, {});
  }

  // ── Lookup Lists ───────────────────────────────────────────────────────────

  getWoTypes(): Observable<WoType[]> {
    return this.data.getData('wo_type');
  }

  getWoType(id: string): Observable<WoType> {
    return this.data.getData('wo_type', id);
  }

  createWoType(body: Partial<WoType>): Observable<WoType> {
    return this.data.post('wo_type', body);
  }

  updateWoType(id: string, body: Partial<WoType>): Observable<WoType> {
    return this.data.patchDataByArray(`wo_type/${id}`, body);
  }

  deleteWoType(id: string): Observable<any> {
    return this.data.deleteDataById('wo_type', id);
  }

  getWoCategories(): Observable<WoCategory[]> {
    return this.data.getData('wo_category');
  }

  getWoCategory(id: string): Observable<WoCategory> {
    return this.data.getData('wo_category', id);
  }

  createWoCategory(body: Partial<WoCategory>): Observable<WoCategory> {
    return this.data.post('wo_category', body);
  }

  updateWoCategory(id: string, body: Partial<WoCategory>): Observable<WoCategory> {
    return this.data.patchDataByArray(`wo_category/${id}`, body);
  }

  deleteWoCategory(id: string): Observable<any> {
    return this.data.deleteDataById('wo_category', id);
  }

  getUsers(): Observable<any[]> {
    return this.data.getDataWithParams('sys_user', 'pageNumber=0&pageSize=999999');
  }

  getUserBillingRates(params?: { user_id?: string; active?: boolean }): Observable<WoUserBillingRate[]> {
    if (params && Object.keys(params).length) {
      const qs = new URLSearchParams(params as any).toString();
      return this.data.getDataWithParams('wo_user_billing_rate', qs);
    }
    return this.data.getData('wo_user_billing_rate');
  }

  getUserBillingRate(id: string): Observable<WoUserBillingRate> {
    return this.data.getData('wo_user_billing_rate', id);
  }

  createUserBillingRate(body: Partial<WoUserBillingRate>): Observable<WoUserBillingRate> {
    return this.data.post('wo_user_billing_rate', body);
  }

  updateUserBillingRate(id: string, body: Partial<WoUserBillingRate>): Observable<WoUserBillingRate> {
    return this.data.patchDataByArray(`wo_user_billing_rate/${id}`, body);
  }

  deleteUserBillingRate(id: string): Observable<any> {
    return this.data.deleteDataById('wo_user_billing_rate', id);
  }

  // ── Checklist Templates ────────────────────────────────────────────────────

  getChecklistTemplates(): Observable<any[]> {
    return this.data.getData('wo_checklist_template');
  }

  getChecklistTemplate(id: string): Observable<any> {
    return this.data.getData('wo_checklist_template', id);
  }

  createChecklistTemplate(body: object): Observable<any> {
    return this.data.post('wo_checklist_template', body);
  }

  updateChecklistTemplate(id: string, body: object): Observable<any> {
    return this.data.patchDataByArray(`wo_checklist_template/${id}`, body);
  }

  deleteChecklistTemplate(id: string): Observable<any> {
    return this.data.deleteDataById('wo_checklist_template', id);
  }

  getChecklistItems(templateId: string): Observable<any[]> {
    return this.data.getData(`wo_checklist_template/${templateId}/items`);
  }

  createChecklistItem(templateId: string, body: object): Observable<any> {
    return this.data.post(`wo_checklist_template/${templateId}/items`, body);
  }

  updateChecklistItem(templateId: string, itemId: string, body: object): Observable<any> {
    return this.data.patchDataByArray(`wo_checklist_template/${templateId}/items/${itemId}`, body);
  }

  deleteChecklistItem(templateId: string, itemId: string): Observable<any> {
    return this.data.deleteDataById(`wo_checklist_template/${templateId}/items`, itemId);
  }
}
